import React, { FunctionComponent, ReactNode, useState } from 'react'
import { ImageSourcePropType } from 'react-native'

import { CSSObject } from 'styled-components'
import styled, { useTheme } from 'styled-components/native'

import { BodyText } from '../../atoms/bodyText/BodyText'
import { ChevronV2Icon } from '../../atoms/icons/ChevronV2Icon'
import { loadIllustrationComponent } from '../../atoms/illustrations/loadIllustrationComponent'
import { Image } from '../../atoms/image/Image'
import { PressableOpacity } from '../../atoms/pressableOpacity/PressableOpacity'
import { Title, Size as TitleSize } from '../../atoms/title/Title'
import { AccessibilityRolesNative } from '../../constants'
import { BodyTextSize } from '../../styles/typeStyles'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'

export type ActionCardProps = {
  id?: string
  title?: string | ReactNode
  onPress?: () => void
  pressedOpacity?: number
  ImageSource?: ImageSourcePropType | string
  iconSize?: number
  withChervon?: boolean
  chevronSize?: number
  disabled?: boolean
  disabledBackgroundColor?: string
  backgroundColor?: string
  customStyle?: CSSObject
  contentCustomStyle?: CSSObject
  chevronMarginLeft?: number
  customImageProps?: Record<string, any>
  useBodyTextTitle?: boolean
}

type ActionCardContainerProps = {
  $hoveredIn: boolean
  disabled: boolean
  backgroundColor: string
  customStyle?: CSSObject
}

const ActionCardContent = styled.View<{ theme: ThemeType; contentCustomStyle?: CSSObject }>(
  ({ theme: { spacing }, contentCustomStyle }) => ({
    padding: spacing['16px'],
    flex: '1',
    flexDirection: 'row',
    alignItems: 'center',
    ...contentCustomStyle,
  }),
)

const CardImage = styled(Image)<{ size: number }>(({ size }) => ({
  width: size,
  height: size,
}))

const CardImageContainer = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  marginRight: spacing['16px'],
}))

const CardDescription = styled.View({ flex: '1', justifyContent: 'center' })

const ChevronContainer = styled.View<{ chevronMarginLeft: number }>(({ chevronMarginLeft }) => ({
  minWidth: '24px',
  alignItems: 'center',
  marginLeft: `${chevronMarginLeft}px`,
  justifyContent: 'center',
}))

const ActionCardContainer = styled(PressableOpacity)<ActionCardContainerProps>(({
  $hoveredIn,
  disabled,
  backgroundColor,
  customStyle,
  theme: {
    breakpoints: { isMinWidthMobileS },
    colors,
    spacing,
  },
}) => {
  const hasShadows: boolean = $hoveredIn
  const rest = {
    minHeight: isMinWidthMobileS ? '94px' : '76px',
    borderRadius: isMinWidthMobileS ? '16px' : '12px',
    border: disabled ? '' : `1px solid ${colors.borderDefault}`,
    backgroundColor,
    flexDirection: 'row' as const,
    marginTop: spacing['16px'],
  }

  const shadows = {
    shadowColor: colors.borderHover,
    shadowOffset: {
      width: '0px',
      height: '2px',
    },
    shadowOpacity: hasShadows ? '0.25' : '0',
    shadowRadius: '10px',
  }
  return { ...rest, ...shadows, ...customStyle }
})

export const ActionCard: FunctionComponent<ActionCardProps> = ({
  id,
  title,
  onPress,
  pressedOpacity = 1,
  ImageSource,
  iconSize = 48,
  withChervon = true,
  chevronSize = 12,
  disabled,
  disabledBackgroundColor,
  backgroundColor,
  customStyle,
  contentCustomStyle,
  customImageProps = {},
  chevronMarginLeft = 16,
  useBodyTextTitle = false,
}): JSX.Element => {
  const [hoveredIn, setHoveredIn] = useState(false)
  const { colors } = useTheme()

  const getImageFromSource = () => {
    const ImageComponent =
      typeof ImageSource === 'string' && loadIllustrationComponent.has(ImageSource)
        ? loadIllustrationComponent(ImageSource)
        : undefined
    if (ImageComponent) {
      return <ImageComponent width={iconSize} height={iconSize} {...customImageProps} />
    } else {
      // Default -- ImageSourcePropType
      return <CardImage source={ImageSource} size={iconSize} />
    }
  }

  return (
    <ActionCardContainer
      onPress={onPress}
      $hoveredIn={hoveredIn}
      pressedOpacity={pressedOpacity}
      pressedBorderColor={colors.primaryButtonBackgroundDefault}
      testID={tID(id)}
      getHoverVisible={(hoverVisible: boolean) => setHoveredIn(hoverVisible)}
      disabled={!!disabled}
      disabledBackgroundColor={disabledBackgroundColor}
      backgroundColor={backgroundColor || colors.backgroundPrimary}
      customStyle={customStyle}
    >
      <ActionCardContent contentCustomStyle={contentCustomStyle}>
        {!!ImageSource && <CardImageContainer>{getImageFromSource()}</CardImageContainer>}
        <CardDescription>
          {useBodyTextTitle ? (
            <BodyText
              size={BodyTextSize.LARGE}
              text={title}
              color={colors.textPrimary}
              role={AccessibilityRolesNative.HEADER}
              level='3'
            />
          ) : (
            <Title size={TitleSize.SMALL} level='3' text={title} color={colors.textPrimary} />
          )}
        </CardDescription>
        {withChervon && (
          <ChevronContainer chevronMarginLeft={chevronMarginLeft}>
            <ChevronV2Icon fillColor={colors.textSecondary} size={chevronSize} />
          </ChevronContainer>
        )}
      </ActionCardContent>
    </ActionCardContainer>
  )
}
